<template>
  <v-container
    id="app-scan"
    class="pa-1"
  >
    <recursive-card
      v-if="action === ActionEnum.giftCredit"
      v-show="showRecursive"
      :client="clientName"
      :card-type="cardType"
      :date="date"
      :amount="formattedAmount"
      :total="nbScan"
      :total-amount="formattedTotalAmount"
      @valid="valid"
    />

    <!-- **********************
    ********** ECRAN **********
    *********************** -->
    <screen
      v-if="!infosCommande"
      :use-amount="useAmount"
      :title="currentView === 'scan-keyboard' ? $t('field.scan.support_number') : null"
      :info="badgeNumberSearch"
      :use-help="action !== ActionEnum.mobil"
      @help="showScanChoice($t('info.scan_man_help'))"
    />

    <!-- ****************************
    ********** RECURSIVITE **********
    ***************************** -->
    <v-card
      v-if="useRecursive"
      v-show="showRecursive"
      class="px-3"
      light
    >
      <div class="d-flex align-center">
        <v-switch
          v-model="recursive"
          :label="$t('field.scan.group_op')"
          color="accent"
          class="d-inline-block py-2 my-1 mr-auto"
          hide-details
        />
        <!-- ******************************
        ********** CLIENT DIALOG **********
        ******************************* -->
        <client-infos
          v-if="useClientInfos"
          :btn-color="clientInfosOk ? 'success' : ''"
          :scan-date="date"
          :scan-name="clientName"
          :show-direct="showDirectClientInfos"
          @show="useNfcListener = false"
          @hide="useNfcListener = true"
          @cancel="showDirectClientInfos ? del() : null"
          @setInfos="setClientInfos"
        />
        <v-btn
          v-else-if="date !== null"
          icon
          outlined
          class="right success mt-2"
          @click="showDateEntry = true"
        >
          <v-icon>mdi-calendar-edit</v-icon>
        </v-btn>
      </div>
      <!-- ***** NB SUPPORT SCANNEES *****  -->
      <div v-show="recursive">
        <v-divider class="mb-2" />
        <div>{{ $t('field.amount_credited') }}: {{ formattedTotalAmount }}</div>
        <div>{{ $t('field.nbr_credited') }}: {{ nbScan }}</div>
      </div>
    </v-card>

    <!-- *********************
    ********** SCAN **********
    ********************** -->
    <contactless
      v-if="currentView === 'scan-nfc'"
      :action="currentAction"
      :credit-only="creditOnly"
      :reset-scan="resetScan"
      :locked="lock"
      :use-listener="useNfcListener && useKeyboardListener"
      :from-intent="intent !== null"
      :total="total"
      @to-lyfpay="toLyfPay"
      @to-scan-choice="showScanChoice"
      @scan="launchScan"
      @del="del"
      @cancel="cancel"
      @valid="valid"
    />
    <qrcode
      v-else-if="currentView === 'scan-qrcode'"
      :action="currentAction"
      :reset-scan="resetScan"
      :total="totalAmount"
      @to-scan-choice="showScanChoice"
      @scan="launchScan"
      @del="del"
      @cancel="cancel"
      @valid="valid"
    />
    <qrcode-usb
      v-else-if="currentView === 'scan-qrcode-usb'"
      :action="currentAction"
      :credit-only="creditOnly"
      :locked="lock"
      :use-listener="useNfcListener && useKeyboardListener"
      :total="totalAmount"
      @scan="launchScan"
      @del="del"
      @cancel="cancel"
      @valid="valid"
    />
    <swipe
      v-else-if="currentView === 'scan-swipe'"
      :action="currentAction"
      :reset-scan="resetScan"
      :total="totalAmount"
      @to-nfc="toNfc"
      @scan="launchScan"
      @del="del"
      @cancel="cancel"
      @valid="valid"
    />
    <keyboard
      v-else
      :action="currentAction"
      :use-listener="useKeyboardListener"
      :use-search="customer === null"
      :id-nom-carte="idNomCarte"
      @hide="showRecursive = false"
      @show="showRecursive = true"
      @code="(code) => { badgeNumberSearch = code }"
      @scan="launchScan"
      @del="del"
      @cancel="cancel"
    />

    <!-- ***************************************
    ********** BOUTONS DE REDIRECTION **********
    **************************************** -->
    <div
      v-if="intent === null && currentView !== 'scan-keyboard' && action !== ActionEnum.invoicePayement && !infosCommande"
      class="bloc pull-bottom top-action d-flex"
    >
      <scan-choice
        :action="currentAction"
        :route="currentView"
        @to-nfc="toNfc"
        @to-qr-scanner="toQrScanner"
        @to-keyboard="toKeyboard"
        @to-customer="toSearchCustomer"
      />
    </div>

    <ui-dialog
      :show="delTry > 2"
      title="Attention"
      danger
      btn-validate="Recharger"
      btn-cancel="Annuler"
      @hide="successDialog"
      @validate="delTry = 0"
    >
      La carte bancaire du client a été débitée pour un montant de {{ $amount() }}.<br><br>
      Cliquez sur <b>Recharger</b> pour finaliser la recharge de son  support.<br><br>
      Cliquez sur <b>Annuler</b> pour mettre fin à la transaction.<br>
      Veuillez contacter un administrateur afin de gérer ce problème.
    </ui-dialog>

    <!-- ******************************
    ********** TICKET DIALOG **********
    ******************************* -->
    <ticket-dialog
      v-model="showTicket"
      :tickets="[currentTicket]"
      :carrier="currentCarrier"
      @close="closeTicket"
    />

    <!-- ****************************
    ********** DATE DIALOG **********
    ***************************** -->
    <date-dialog
      v-model="showDateEntry"
      btn-class="right success"
      hide-overlay
      outlined
      :use-activator="!useClientInfos && date !== null"
      :use-listener="showDateEntry"
      :default-date="dateData ? dateData.defaultDate : null"
      @del="closeDate"
      @cancel="cancel"
      @valid="checkDate"
    />

    <!-- ********************************
    ********** CODE PIN DIALOG **********
    ********************************* -->
    <keyboard-dialog
      ref="keyboardDialog"
      v-model="showPinEntry"
      use-default-screen
      keyboard-code
      secure-code
      :use-listener="showPinEntry"
      :title="$t('field.scan.pin_code_capture', { type: pinData.type })"
      :screen-title="$t('field.scan.pin_code', { type: pinData.type })"
      @close="closePin"
      @valid="checkPin"
    />

    <!-- ***********************************
    ********** SCAN CHOICE DIALOG **********
    ************************************ -->
    <scan-choice-dialog
      v-model="scanDialog.show"
      :message="scanDialog.message"
      :index-error="scanDialog.scanIndex"
      @hide="del"
      @action="currentView = initialView = arguments[0]"
    />
  </v-container>
</template>

<script>
import QRCode from 'qrcode'
import SimpleCrypto from 'simple-crypto-js'

import { $alert, $amount, createReference, $formatDate, $formatTime } from '@/modules/Utils'
import { ActionEnum, MaxTryPinCode, MODE, SCAN_TYPE, ERRORKEY, ServerEnum } from '@/modules/Enums'
import { Scan } from '@/modules/Scan'
import { Carrier } from '@/modules/Carrier'
import { Ticket } from '@/modules/Ticket'

import Contactless from '@/components/scan/Contactless.vue'
import Qrcode from '@/components/scan/Qrcode.vue'
import Keyboard from '@/components/scan/Keyboard.vue'
import Swipe from '@/components/scan/Swipe.vue'
import QrcodeUsb from '@/components/scan/QrcodeUsb.vue'

import ClientInfos from '@/components/modal/ClientInfos.vue'
import TicketDialog from '@/components/modal/TicketDialog.vue'
import KeyboardDialog from '@/components/modal/KeyboardDialog.vue'
import DateDialog from '@/components/modal/DateDialog.vue'
import ScanChoiceDialog from '@/components/modal/ScanChoiceDialog.vue'
import ScanChoice from '@/components/scan/ScanChoice.vue'
import RecursiveCard from '@/components/scan/RecursiveCard.vue'

export default {
  name: 'ScanView',

  components: {
    RecursiveCard,
    ScanChoice,
    QrcodeUsb,
    Swipe,
    ScanChoiceDialog,
    Contactless,
    Qrcode,
    Keyboard,
    //
    TicketDialog,
    KeyboardDialog,
    DateDialog,
    ClientInfos
  },

  props: {
    action: { type: Number, default: null },
    //
    idBadge: { type: String, default: null },
    mode: { type: String, default: null },
    creditOnly: { type: Boolean, default: false },
    backToHome: { type: Boolean, default: false },
    backToNfcOffline: { type: Boolean, default: false },
    fromAchat: { type: Boolean, default: false },
    reference: { type: String, default: null },
    //
    customer: { type: Object, default: null },
    customers: { type: Array, default: null },
    creditGiftParams: { type: Object, default: null }
  },

  data () {
    return {
      ActionEnum,
      cancellable: true,
      deletable: true,
      useAmount: true,
      initialView: this.$route.name,
      currentView: this.$route.name,
      currentAction: this.action,
      //
      showDateEntry: false,
      dateData: null,
      date: null,
      clientName: null,
      cardType: null,
      badgeNumberSearch: '',
      //
      useRecursive: false, // détermine si on autorise l'utilisation de la vignette de récursivité
      showRecursive: true, // utilisé lors de l'affichage de plusieurs cartes (badge recherche)
      recursive: false, // booléen qui controle l'utilisation de la récursivité
      useClientInfos: false,
      showDirectClientInfos: false,
      cardsNumber: [], // numéro des cartes scannées en récursif (contrôle double scan)
      cardsId: [], // id des cartes scannées en récursif (contrôle double scan)
      total: 0, // montant total crédits effectués en récursif
      idCissCommande: null,
      //
      lock: false,
      resetScan: false,
      useNfcListener: true,
      //
      showTicket: false,
      currentTicket: null,
      currentCarrier: null,
      //
      showPinEntry: false,
      pinData: { type: '', code: null },
      pinTry: 1,
      delTry: 0,
      referenceTransaction: this.reference ? this.reference : createReference(),
      //
      scanDialog: {
        show: false,
        message: null,
        scanIndex: null
      },
      tickets: [],
    }
  },

  computed: {
    amount () { return this.$store.state.amount },
    formattedAmount() {
      if (this.creditGiftParams === null) {
        return this.$amount(this.amount)
      }
      if (this.creditGiftParams.typeSupport === ServerEnum.typePorteMonnaieCarte) {
        return this.$amount(this.creditGiftParams.montant)
      }
      if (parseInt(this.amount) > 0) {
        return this.$amount(this.amount)
      }
      return '---'
    },
    intent () { return this.$store.getters.externalAction ? this.$store.state.intent : null },
    clientInfosOk () {
      return this.clientName !== null && this.date !== null
    },
    useKeyboardListener () {
      return !this.showDateEntry && !this.showPinEntry
    },
    nbScan () {
      return this.cardsId.length
    },
    totalAmount () {
      return this.nbScan * this.amount
    },
    formattedTotalAmount () {
      return $amount(this.totalAmount)
    },
    idNomCarte () {
      return this.creditGiftParams !== null ? this.creditGiftParams.idNomCarte : null
    },
    infosCommande () {
      return this.action === ActionEnum.commandeInformations
    }
  },

  created () {
    if (this.currentAction === null) {
      this.$router.replace('/')
    }
  },

  beforeMount () {
    if (
      this.action === ActionEnum.addClientCard ||
      this.action === ActionEnum.invoicePayement ||
      this.action === ActionEnum.badgeInformations ||
      this.action === ActionEnum.clientInformations ||
      this.action === ActionEnum.mobil ||
      this.action === ActionEnum.voucher ||
      this.action === ActionEnum.gift
    ) {
      this.useAmount = false
    }

    if (
      (this.action === ActionEnum.cashlessReset && this.amount > 0) ||
      this.action === ActionEnum.cashlessCredit
    ) {
      if (this.action !== ActionEnum.cashlessCredit) {
        this.useNfcListener = false
        this.showDirectClientInfos = true
      } else if (IS_PAX && this.mode === MODE.CB && this.creditOnly) {
        this.cancellable = false
        this.deletable = false
      }
      this.useClientInfos = true
    }

    // Détermination de la récursivité
    // (chèques dedit/credit, cartes credit et scan direct)
    if (this.action === ActionEnum.cashlessReset || this.action === ActionEnum.giftReset || this.action === ActionEnum.giftCredit) {
      this.recursive = true
    }

    // Activation de la récursivité
    if (
      this.action === ActionEnum.giftReset ||
      this.action === ActionEnum.cashlessReset ||
      (this.action === ActionEnum.cashlessCredit && this.mode !== MODE.CB)
    ) {
      this.useRecursive = true
    }

    if (this.action === ActionEnum.giftCredit) {
      this.clientName = this.creditGiftParams.nomClient
      this.date = this.creditGiftParams.dateFinValid
      this.cardType = this.creditGiftParams.labelTypeCarte
      this.deletable = true
      this.useAmount = false
    }
  },

  methods: {
    $formatDate,
    $formatTime,
    $amount,
    successDialog () {
      // Ici, je crée le ticket avec la référence de transaction.
      const crypto = new SimpleCrypto(ERRORKEY)
      const reference = createReference()
      const code = 'COR_' + crypto.encrypt(this.amount + '_' + reference)

      QRCode.toDataURL(code, { scale: 8, errorCorrectionLevel: 'H' }).then(result => {
        this.currentTicket = {
          Image: result.replace('data:image/png;base64,', ''),
          isPNG: true,
          Lignes: [
            { Msg: 'TICKET ERREUR', Align: 'C', Taille: 3 },
            { Msg: ' ', Align: 'L', Taille: 1 },
            { Msg: 'DEBIT CB:                     OK', Align: 'L', Taille: 1 },
            { Msg: 'CREDIT SUPPORT:           ERREUR', Align: 'L', Taille: 1 },
            { Msg: '--------------------------------', Align: 'L', Taille: 1 },
            { Msg: `Votre carte bancaire a été débitée pour un montant de ${$amount()}.`, Align: 'L', Taille: 1 },
            { Msg: ' ', Align: 'L', Taille: 1 },
            { Msg: `La recharge de votre support n'apas été effectuée.`, Align: 'L', Taille: 1 },
            { Msg: ' ', Align: 'L', Taille: 1 },
            { Msg: `Veuillez contacter un administrateur afin de résoudre ce problème en lui présentant ce QR Code.`, Align: 'L', Taille: 1 }
          ]
        }
        this.$router.replace({
          name: 'ticket',
          params: { tickets: [this.currentTicket].concat(Ticket.bankingTickets), startPrint: true }
        })
      })
    },
    del () {
      // condition qui est set par le pax lors du before mount
      // c'est au moment ou on a accepté la CB et qu'on fait le crédit d'une carte.
      // deletable est a false si il est arrivé par le mode CB
      if (this.deletable) {
        if (this.currentView === this.initialView) {
          if (this.backToHome) {
            this.$router.replace({
              name: 'home',
              params: { reset: false }
            })
          } else if (this.backToNfcOffline) {
            this.$emit('title', this.$i18n.t('button.cashless.debit_display'))
            this.$router.replace({
              name: 'scan-nfc-offline',
              params: { action: ActionEnum.cashlessDebit }
            })
          } else if (this.currentAction === ActionEnum.clientInformations) {
            this.$router.replace('/client-search')
          } else if (this.customers) {
            this.$router.replace({
              name: 'customer-search',
              params: { customers: this.customers }
            })
          } else if (this.currentAction === ActionEnum.addClientCard) {
            this.toCustomer()
          } else if (this.currentAction === ActionEnum.giftCredit) {
            this.$router.replace({
              name: 'credit-gift',
              params: { creditGiftParams: this.creditGiftParams }
            })
          } else {
            this.$router.replace({
              name: 'action-choice',
              params: { fromAchat: this.fromAchat }
            })
          }
        } else {
          if (this.action !== this.currentAction) {
            // Only Lyfpay change the current action, if back click, we comeback to cashless payment
            this.$emit('title', this.$i18n.t('button.cashless.debit_display'))
            this.currentAction = this.action
          }

          this.currentView = this.initialView
        }
      } else {
        // Ici on appuie sur le bouton jaune pour revenir sur l'ecran d'avant
        // mais ce n'est pas possible car on vient d'accepter la cb
        // on incremente la tentative de suppression
        // Quand delTry devient superieur à 5 on affiche une modal
        this.delTry++
      }
    },
    cancel () {
      if (this.currentAction === ActionEnum.giftCredit) {
        if (this.nbScan === 0)  this.$router.replace('/home')
        return
      }

      // condition qui est set par le pax lors du before mount
      // c'est au moment ou on a accepté la CB et qu'on fait le crédit d'une carte.
      // Cancellable est a false quand on arrive depuis la CB
      if (this.cancellable) {
        this.$router.replace('/home')
      } else {
        // Ici on essaie d'appuyer sur le bouton rouge pour revenir
        // mais ce n'est pas possible car on a encaissé la CB
        // on incremente la tentative de suppression
        // Quand delTry devient superieur à 5 on affiche une modal
        this.delTry++
      }
    },
    //
    toLyfPay () {
      this.$emit('title', this.$i18n.t('button.lyfpay.debit_display'))
      this.currentAction = ActionEnum.lyfpayDebit
      this.currentView = 'scan-qrcode'
    },
    toQrScanner (lock = false) {
      this.currentView = 'scan-qrcode'
      if (lock) this.initialView = 'scan-qrcode'
    },
    toNfc (lock = false) {
      this.currentView = 'scan-nfc'
      if (lock) this.initialView = 'scan-nfc'
    },
    toKeyboard (lock = false) {
      this.currentView = 'scan-keyboard'
      if (lock) this.initialView = 'scan-keyboard'
    },
    toSearchCustomer () {
      this.$router.replace({
        name: 'customer-search',
        params: { scanProps: { ...this.$props, route: this.initialView } }
      })
    },
    toCustomer () {
      this.$router.replace({
        name: 'customer',
        params: { customer: this.customer }
      })
    },
    showScanChoice (message, index = null) {
      this.scanDialog.message = message
      this.scanDialog.show = true
      this.scanDialog.scanIndex = index
    },
    //
    setClientInfos (clientName, date) {
      this.clientName = clientName
      this.date = date
    },
    closeTicket () {
      // Devérouillage scan
      this.lock = false
      // Restart scan
      this.resetScan = !this.resetScan
      // Fermeture du ticket
      this.showTicket = false
      this.currentTicket = null
      this.currentCarrier = null
    },
    //
    closePin () {
      this.showPinEntry = false
      // Restart scan
      this.lock = false
      this.resetScan = !this.resetScan
    },
    checkPin (code) {
      if (this.pinData.code === null) {
        // Save pin for add client card
        this.showPinEntry = false
        this.pinData.code = code
        this.launchScan(this.pinData.uid, 'MAN')
      } else if (code === this.pinData.code) {
        this.showPinEntry = false
        // Reprise débit/crédit
        this.continueScan(this.pinData.badge, this.pinData.infos)
      } else {
        if (this.pinTry === MaxTryPinCode) {
          $alert(this.$i18n.t('error.bad_pin_max', { type: this.type }))
          this.closePin()
        } else {
          $alert(this.$i18n.t('error.bad_pin', { type: this.type, try: (MaxTryPinCode - this.pinTry) }))
          this.pinTry++
          this.$refs.keyboardDialog.reset()
        }
      }
    },
    //
    closeDate () {
      this.showDateEntry = false
      // Restart scan
      this.lock = false
      this.resetScan = !this.resetScan
    },
    checkDate (date) {
      let continueScan = this.date === null
      this.date = new Date(date).toISOString().substr(0, 10)
      this.showDateEntry = false
      if (continueScan) {
        // Reprise débit/crédit
        this.continueScan(this.dateData.badge, this.dateData.infos)
      }
    },
    //
    launchScan (uid, type = SCAN_TYPE.NFC) {
      // Contrôle verrouillage
      if (this.lock) {
        return
      }

      // Fermeture d'une éventuelle alerte
      $alert(false)

      // Si on est en mode paiement de facture, on retourne vers home pour lancer l'appli CB
      if (this.action === ActionEnum.invoicePayement) {
        const res = uid.replace(/[\s|"|']/g, '').split(';')
        if (res[0] && res[1]) {
          this.$store.commit('amount', parseFloat(res[1].replace(',', '.')) * 100)
          this.$router.replace({
            name: 'banking-payment',
            params: { reference: res[0] }
          })
        }
        return
      }

      // Si pas déjà scanné
      if (this.cardsId.indexOf(uid) !== -1) {
        this.$store.commit('loader', false)
        $alert(this.$i18n.t('error.support_scanned'))
        // Restart scan
        this.resetScan = !this.resetScan
        return
      }

      // Contrôle informations de réinitialisation saisies
      if (
        this.useClientInfos &&
        this.action !== ActionEnum.cashlessCredit &&
        (this.clientName === null || this.date === null)
      ) {
        $alert(this.$i18n.t('error.name_and_date_required'))
        return
      }

      if (this.currentAction === ActionEnum.cashlessTransfert) {
        Scan.transferSolde(this.idBadge, uid, this.amount, this.referenceTransaction)
          .then(
            _ => {},
            this.errorScan
          )
        return
      }

      if (this.currentAction === ActionEnum.addClientCard) {
        if (this.currentView === 'scan-keyboard' && this.pinData.code === null) {
          // this.pinData.type = 'PIN'
          this.pinData.uid = uid
          this.showPinEntry = true
          this.lock = false
        } else {
          Carrier.addContactCard(
            this.customer.IDContact,
            uid,
            _ => {
              $alert(this.$i18n.t('success.add_card'), 'success')
              this.toCustomer()
            },
            this.errorScan,
            type,
            'FID',
            this.pinData.code
          )
        }
        return
      }

      // Si on rentre en mode correction
      // traitement d'un cas ou avec un PAX le bancaire a été accepté
      // mais le caissier n'a pas réussis a créditer le solde sur une carte
      // il a imprimé un ticket d'echec que le manager viens de scanner
      // La on va traiter le ticket
      if (type === SCAN_TYPE.QR_CODE && uid.startsWith('COR_')) {
        uid = uid.replace('COR_', '')
        const crypto = new SimpleCrypto(ERRORKEY)
        const data = crypto.decrypt(uid).split('_')
        // j'insère le montant
        this.$store.commit('amount', parseInt(data[0]))
        this.referenceTransaction = data[1]
        this.amount = data[0]
        this.useAmount = true
        this.action = ActionEnum.cashlessCredit
        this.currentAction = ActionEnum.cashlessCredit
        this.mode = '1'
        this.creditOnly = true
        this.lock = false
        this.toNfc()
        return
      }

      if ((uid.startsWith('MP')) || (uid.startsWith('MPC_KDO'))) {
        Scan.getCommandeInfos(
          uid,
          this.successScan,
          this.errorScan
        )
        return
      }

      // Scan classique
      let typeSupport = null
      let idNomCarte = null
      if (this.action === ActionEnum.giftCredit) {
        typeSupport = this.creditGiftParams.typeSupport
        idNomCarte = this.creditGiftParams.idNomCarte
      }

      // Verrouillage NFC
      this.lock = true

      // ***** SCAN *****
      Scan.solde(
        uid,
        this.currentAction,
        this.mode,
        this.successScan,
        this.errorScan,
        this.currentView === 'scan-keyboard',
        this.recursive,
        this.date,
        this.clientName,
        this.referenceTransaction,
        type,
        this.fromAchat,
        this.idCissCommande,
        typeSupport,
        idNomCarte
      )
    },
    continueScan (badgeNumber, badgeInfoResponse) {
      Scan.manageBadge(
        badgeNumber,
        this.currentAction,
        this.mode,
        badgeInfoResponse,
        this.successScan,
        this.errorScan,
        this.recursive,
        this.date,
        this.clientName,
        this.referenceTransaction,
        this.fromAchat
      )
    },
    successScan (response) {
      if (response !== null && response.id) {
        this.cardsId.push(response.id)
        if (response.action && (response.action === ActionEnum.reset || response.action === ActionEnum.giftCredit)) {
          if (this.action === ActionEnum.giftCredit) {
            this.idCissCommande = response.idCissCommande
            this.tickets.push(response.Ticket)
            this.cardsNumber.push(response.NumeroCarte)
          } else {
            $alert(
              this.$i18n.t(this.action === ActionEnum.giftReset ? 'success.reinit_gift' : 'success.reinit_cashless'),
              'success'
            )
          }
          if (this.recursive) {
            this.lock = false
            this.resetScan = !this.resetScan
          } else {
            this.cancel()
          }
        } else {
          this.total += response.amount
          // Display ticket
          this.currentTicket = response.ticket
          this.currentCarrier = response.carrier
          this.showTicket = true
        }
        // Au cas ou on ai fait la transaction de correction
        // On supprime la ref de transaction d'un qr_code COR_{quelque choses}
        this.referenceTransaction = createReference()
      }
    },
    errorScan (response, data = null) {
      if (response === 'pin_required') {
        // Saisie du code PIN/CCV
        this.pinData = data
        this.showPinEntry = true
      } else if (response === 'date_required') {
        // Saisie d'une date de fin de validité'
        this.dateData = data
        this.showDateEntry = true
      } else {
        $alert(response)
        this.lock = false
        this.pinData.code = null
        // Restart scan
        this.resetScan = !this.resetScan
      }
    },
    valid() {
      if (this.action === ActionEnum.giftCredit) {
        if (this.tickets.length > 0) {
          this.ticketsMerge()
        }
        this.$store.commit('amount', 0)
        this.cardsId = []
        this.cardsNumber = []
        this.idCissCommande = null
        if (this.tickets.length > 0) {
          this.$router.replace({ name: 'ticket', params: { tickets: this.tickets } })
        } else {
          this.$router.replace('/home')
        }
      }
    },
    ticketsMerge () {
      let ticketsCreated = { Lignes: [], Nb: 0 }
      ticketsCreated.Lignes.push({'Align': 'C', 'Gras': 0, 'Img' : '', 'Ligne': 1, 'Msg': 'RECHARGEMENT ACCEPTE', 'Taille': '2'})
      ticketsCreated.Lignes.push({'Align': 'L', 'Gras': 0, 'Img' : '', 'Ligne': 2, 'Msg': 'LE ' + $formatDate() + ' A ' + $formatTime(), 'Taille': '2'})
      ticketsCreated.Lignes.push({'Align': 'L', 'Gras': 0, 'Img' : '', 'Ligne': 3, 'Msg': this.$store.getters.enseigne, 'Taille': '2'})
      ticketsCreated.Lignes.push({'Align': 'L', 'Gras': 0, 'Img' : '', 'Ligne': 4, 'Msg': 'Numéro de commande: ' + this.idCissCommande, 'Taille': '2'})
      ticketsCreated.Lignes.push({'Align': 'L', 'Gras': 0, 'Img' : '', 'Ligne': 5, 'Msg': 'Demandeur: ' + this.creditGiftParams.nomClient, 'Taille': '2'})
      ticketsCreated.Lignes.push({'Align': 'L', 'Gras': 0, 'Img' : '', 'Ligne': 6, 'Msg': 'Fin validité: ' + $formatDate(this.creditGiftParams.dateFinValid), 'Taille': '2'})
      ticketsCreated.Lignes.push({'Align': 'L', 'Gras': 0, 'Img' : '', 'Ligne': 7, 'Msg': 'Montant rechargement: ' + $amount(this.amount), 'Taille': '2'})

      this.cardsNumber.forEach((numero, i) => {
        ticketsCreated.Lignes.push({'Align': 'L', 'Gras': 0, 'Img' : '', 'Ligne': (8 + i), 'Msg': '    - ' + numero, 'Taille': '2'})
      })
      ticketsCreated.Lignes.push({'Align': 'L', 'Gras': 0, 'Img' : '', 'Ligne': this.nbScan + 9, 'Msg': 'Nombre de titres scannés: ' + this.nbScan, 'Taille': '2'})
      ticketsCreated.Lignes.push({'Align': 'L', 'Gras': 0, 'Img' : '', 'Ligne': this.nbScan + 10, 'Msg': 'Montant total rechargé: ' + this.formattedTotalAmount, 'Taille': '2'})
      ticketsCreated.Lignes.push({'Align': 'C', 'Gras': 0, 'Img' : '', 'Ligne': this.nbScan + 11, 'Msg': 'MERCI A BIENTOT', 'Taille': '2'})
      ticketsCreated.Nb = this.nbScan + 12

      this.tickets = [ticketsCreated]
    }
  }
}
</script>
